import React, { useState } from 'react';
import { Dialog, DialogContent, Box, Typography, Button, TextField, CircularProgress } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudIcon from '@mui/icons-material/Cloud';
import FolderIcon from '@mui/icons-material/Folder';
import StorageIcon from '@mui/icons-material/Storage';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

interface CloudGatewayIntroModalProps {
  open: boolean;
  onComplete: (workspaceId: string) => void;
  onCreateWorkspace: (name: string) => Promise<{ success: boolean; workspace?: { id: string; name: string } }>;
  userName?: string;
  onBackToMain?: () => void;
}

const CloudGatewayIntroModal: React.FC<CloudGatewayIntroModalProps> = ({
  open,
  onComplete,
  onCreateWorkspace,
  userName = 'User',
  onBackToMain,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [workspaceName, setWorkspaceName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');

  const firstName = userName?.split(' ')[0] || 'User';

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCreateWorkspace = async () => {
    if (!workspaceName.trim()) {
      setError('Please enter a workspace name');
      return;
    }

    setIsCreating(true);
    setError('');

    try {
      const result = await onCreateWorkspace(workspaceName.trim());
      if (result.success && result.workspace) {
        // Mark intro as completed
        try {
          localStorage.setItem('cloudGatewayIntroCompleted', 'true');
        } catch { /* ignore */ }
        onComplete(result.workspace.id);
      } else {
        setError('Failed to create workspace. Please try again.');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  if (!open) return null;

  const steps = [
    // Step 0: Welcome
    {
      title: `Welcome to Cloud Gateway, ${firstName}!`,
      content: (
        <Box sx={{ textAlign: 'center' }}>
          <Box
            sx={{
              width: 120,
              height: 120,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 4,
              boxShadow: '0 12px 40px rgba(79, 172, 254, 0.4)',
            }}
          >
            <CloudIcon sx={{ fontSize: 60, color: 'white' }} />
          </Box>
          <Typography sx={{ fontSize: '16px', color: '#535862', lineHeight: 1.7, maxWidth: 450, mx: 'auto' }}>
            Cloud Gateway is your <strong>central hub</strong> for managing all your cloud resources, 
            AI services, and data repositories in one unified platform.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 4 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{ width: 48, height: 48, borderRadius: '12px', bgcolor: '#F0F9FF', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1 }}>
                <StorageIcon sx={{ color: '#4facfe' }} />
              </Box>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>Cloud Storage</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{ width: 48, height: 48, borderRadius: '12px', bgcolor: '#FDF4FF', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1 }}>
                <SmartToyIcon sx={{ color: '#A855F7' }} />
              </Box>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>AI Models</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{ width: 48, height: 48, borderRadius: '12px', bgcolor: '#F0FDF4', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1 }}>
                <FolderIcon sx={{ color: '#10B981' }} />
              </Box>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>Repositories</Typography>
            </Box>
          </Box>
        </Box>
      ),
    },
    // Step 1: Concepts - Resources
    {
      title: 'Understanding Resources',
      content: (
        <Box>
          <Typography sx={{ fontSize: '15px', color: '#535862', lineHeight: 1.7, mb: 3 }}>
            In Cloud Gateway, <strong>resources</strong> are the building blocks of your infrastructure:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, p: 2, bgcolor: '#F9FAFB', borderRadius: '12px' }}>
              <Box sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: '#EEF2FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <StorageIcon sx={{ color: '#617BFF', fontSize: 22 }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#181D27', mb: 0.5 }}>Buckets</Typography>
                <Typography sx={{ fontSize: '13px', color: '#535862', lineHeight: 1.5 }}>
                  Connect your cloud storage from AWS S3, Google Cloud, or Azure. Store and manage your data securely.
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, p: 2, bgcolor: '#F9FAFB', borderRadius: '12px' }}>
              <Box sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: '#FDF4FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <SmartToyIcon sx={{ color: '#A855F7', fontSize: 22 }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#181D27', mb: 0.5 }}>AI Configurations</Typography>
                <Typography sx={{ fontSize: '13px', color: '#535862', lineHeight: 1.5 }}>
                  Configure AI models from OpenAI, Anthropic, and more. Use them through a unified, secure gateway.
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, p: 2, bgcolor: '#F9FAFB', borderRadius: '12px' }}>
              <Box sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: '#F0FDF4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <FolderIcon sx={{ color: '#10B981', fontSize: 22 }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#181D27', mb: 0.5 }}>Repositories</Typography>
                <Typography sx={{ fontSize: '13px', color: '#535862', lineHeight: 1.5 }}>
                  Group your buckets and AI configurations together. Generate API keys for secure programmatic access.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      ),
    },
    // Step 2: Concepts - Workspaces
    {
      title: 'Understanding Workspaces',
      content: (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
            <Box
              sx={{
                width: 100,
                height: 100,
                borderRadius: '50%',
                bgcolor: 'rgba(97, 123, 255, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <WorkspacesIcon sx={{ fontSize: 50, color: '#617BFF' }} />
            </Box>
          </Box>
          <Typography sx={{ fontSize: '15px', color: '#535862', lineHeight: 1.7, mb: 3, textAlign: 'center' }}>
            A <strong>Workspace</strong> is your organizational container that groups all related resources together.
          </Typography>
          <Box sx={{ bgcolor: '#F0F2FF', borderRadius: '12px', p: 3, mb: 3 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#617BFF', mb: 2 }}>
              Why use Workspaces?
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {[
                'Organize resources by project, team, or environment',
                'Control access and permissions at the workspace level',
                'Track usage and costs per workspace',
                'Isolate production from development resources',
              ].map((item, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <CheckCircleIcon sx={{ fontSize: 18, color: '#617BFF' }} />
                  <Typography sx={{ fontSize: '13px', color: '#374151' }}>{item}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      ),
    },
    // Step 3: Create Workspace
    {
      title: 'Create Your First Workspace',
      content: (
        <Box>
          <Typography sx={{ fontSize: '15px', color: '#535862', lineHeight: 1.7, mb: 4, textAlign: 'center' }}>
            Let's get started! Create your first workspace to begin managing your cloud resources.
          </Typography>
          <Box sx={{ maxWidth: 400, mx: 'auto' }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#374151', mb: 1 }}>
              Workspace Name
            </Typography>
            <TextField
              fullWidth
              value={workspaceName}
              onChange={(e) => {
                setWorkspaceName(e.target.value);
                setError('');
              }}
              placeholder="e.g., My Project, Development, Production..."
              error={!!error}
              helperText={error}
              disabled={isCreating}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  bgcolor: '#FAFAFA',
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': { borderColor: '#617BFF' },
                  '&.Mui-focused fieldset': { borderColor: '#617BFF' },
                },
              }}
            />
            <Typography sx={{ fontSize: '12px', color: '#9CA3AF', mt: 1 }}>
              You can create more workspaces later and rename this one anytime.
            </Typography>
          </Box>
        </Box>
      ),
    },
  ];

  const currentStepData = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          maxWidth: '560px',
          overflow: 'hidden',
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Progress bar */}
        <Box sx={{ display: 'flex', gap: 0.5, p: 2, pb: 0 }}>
          {steps.map((_, idx) => (
            <Box
              key={idx}
              sx={{
                flex: 1,
                height: 4,
                borderRadius: 2,
                bgcolor: idx <= currentStep ? '#617BFF' : '#E5E7EB',
                transition: 'background-color 0.3s ease',
              }}
            />
          ))}
        </Box>

        {/* Content */}
        <Box sx={{ p: 4, pt: 3 }}>
          <Typography
            sx={{
              fontSize: '24px',
              fontWeight: 700,
              color: '#181D27',
              mb: 3,
              textAlign: 'center',
            }}
          >
            {currentStepData.title}
          </Typography>

          {currentStepData.content}
        </Box>

        {/* Navigation */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 3,
            pt: 0,
            gap: 2,
          }}
        >
          {currentStep === 0 && onBackToMain ? (
            <Button
              variant="text"
              onClick={onBackToMain}
              startIcon={<ArrowBackIcon />}
              sx={{
                textTransform: 'none',
                color: '#717680',
                fontWeight: 500,
                fontSize: '13px',
                px: 2,
                '&:hover': {
                  color: '#535862',
                  bgcolor: 'rgba(0,0,0,0.04)',
                },
              }}
            >
              Back to Main
            </Button>
          ) : (
            <Button
              variant="outlined"
              onClick={handleBack}
              disabled={currentStep === 0 || isCreating}
              startIcon={<ArrowBackIcon />}
              sx={{
                visibility: currentStep === 0 ? 'hidden' : 'visible',
                textTransform: 'none',
                borderRadius: '10px',
                borderColor: '#D5D7DA',
                color: '#535862',
                fontWeight: 600,
                px: 3,
                '&:hover': {
                  borderColor: '#617BFF',
                  bgcolor: 'rgba(97, 123, 255, 0.04)',
                },
              }}
            >
              Back
            </Button>
          )}

          <Typography sx={{ fontSize: '13px', color: '#9CA3AF' }}>
            Step {currentStep + 1} of {steps.length}
          </Typography>

          {isLastStep ? (
            <Button
              variant="contained"
              onClick={handleCreateWorkspace}
              disabled={isCreating || !workspaceName.trim()}
              sx={{
                textTransform: 'none',
                borderRadius: '10px',
                bgcolor: '#617BFF',
                fontWeight: 600,
                px: 3,
                minWidth: 160,
                boxShadow: '0 4px 12px rgba(97, 123, 255, 0.3)',
                '&:hover': {
                  bgcolor: '#5068e0',
                },
                '&.Mui-disabled': {
                  bgcolor: '#E5E7EB',
                  color: '#9CA3AF',
                },
              }}
            >
              {isCreating ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                'Create Workspace'
              )}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              endIcon={<ArrowForwardIcon />}
              sx={{
                textTransform: 'none',
                borderRadius: '10px',
                bgcolor: '#617BFF',
                fontWeight: 600,
                px: 3,
                boxShadow: '0 4px 12px rgba(97, 123, 255, 0.3)',
                '&:hover': {
                  bgcolor: '#5068e0',
                },
              }}
            >
              Next
            </Button>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CloudGatewayIntroModal;
